import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyEdgeChanges,
  applyNodeChanges,
  Edge,
  MarkerType,
  Node,
  OnEdgesChange,
  OnNodesChange
} from "reactflow";
import "reactflow/dist/style.css";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ReactFlowProvider } from "reactflow";
import GraphCanvas from "components/Graph/GraphCanvas";
import InfluencesModal from "components/Issues/InfluencesModal";
import { GraphIssueNodeData } from "components/Graph/GraphIssueNode";
import { Issue } from "util/constants";
import { getIssueRankings, IssueRanking } from "util/userUtils";
import {
  fetchIssueByCname,
  fetchIssueInfluenceTotals,
  fetchIssueInfluences,
  fetchGraphRoots,
  GraphIssue,
  GraphRootsGroup,
  InfluenceDirection,
  InfluenceTotals
} from "util/graphData";
import { setDefaultSiteOg, setDocumentTitle } from "util/ogMetadataUtils";
import ExperimentalFeatureBanner from "components/Common/ExperimentalFeatureBanner";
import GraphExplorerFeed from "components/Graph/GraphExplorerFeed";
import GraphExplorerEdit from "components/Graph/GraphExplorerEdit";
import GraphDraftEditor from "components/Graph/GraphDraftEditor";
import { graphMessage, resolveGraphLocale } from "components/Graph/graphCopy";
import useMobileViewport from "hooks/useMobileViewport";
import LoadingSkeleton from "ui/LoadingSkeleton";
import { useHideAppBarOnMobile } from "ui/AppChromeContext";
import DraftsList from "components/Drafts/DraftsList";
import type { DbDraft } from "hooks/queries/useDraftsQuery";
import type { LocalDraftSummary } from "util/localDrafts";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";

/* ───────── Constants ───────── */

/** Fixed node width in the custom node component. */
const NODE_W = 200;
/** Estimated node height for layout. */
const NODE_H = 90;
/** Minimum gap between nodes on all sides. */
const NODE_GAP = 40;
/** Horizontal step per depth level. */
const COL_STEP = NODE_W + NODE_GAP;
/** Vertical step between same-column nodes. */
const ROW_STEP = NODE_H + NODE_GAP;
/** Vertical gap between separate connected components in global view. */
const GROUP_GAP = 80;

const DEFAULT_BATCH_SIZE = 6;

/* ───────── Types ───────── */

interface GraphEdgeSeed {
  sourceId: number;
  targetId: number;
}

interface GraphNodeEntry {
  issue: GraphIssue;
  position: { x: number; y: number };
  depth: number;
}

interface EdgeCounts {
  inCount: number;
  outCount: number;
}

/* ───────── Helpers ───────── */

const buildEdgeId = (src: number, tgt: number): string => `${src}->${tgt}`;

const dedupeEdges = (seeds: GraphEdgeSeed[]): GraphEdgeSeed[] => {
  const seen = new Set<string>();
  return seeds.filter((s) => {
    const id = buildEdgeId(s.sourceId, s.targetId);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const computeEdgeCounts = (edges: Edge[]): Record<string, EdgeCounts> => {
  const counts: Record<string, EdgeCounts> = {};
  edges.forEach((edge) => {
    if (!counts[edge.source]) counts[edge.source] = { inCount: 0, outCount: 0 };
    if (!counts[edge.target]) counts[edge.target] = { inCount: 0, outCount: 0 };
    counts[edge.source].outCount += 1;
    counts[edge.target].inCount += 1;
  });
  return counts;
};

const GraphNav: React.FC<{
  mode: "explore" | "feed" | "drafts";
  graphLocale: ReturnType<typeof resolveGraphLocale>;
  draftsLabel: string;
}> = ({ mode, graphLocale, draftsLabel }) => {
  const navClassName =
    "flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-eoe-border-main bg-eoe-bg-modal/90 px-4 py-2 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal/90";
  const activeClassName = "text-sm font-semibold text-eoe-text dark:text-eoe-dark-text";
  const inactiveClassName =
    "text-sm font-semibold text-eoe-text-sub hover:text-eoe-text dark:text-eoe-dark-text-sub dark:hover:text-eoe-dark-text";
  const accentClassName = "text-sm font-semibold text-eoe-accent hover:underline dark:text-eoe-dark-accent";
  const separator = (
    <span className="text-eoe-text-sub dark:text-eoe-dark-text-sub" aria-hidden>
      |
    </span>
  );

  return (
    <nav className={navClassName} aria-label={graphMessage(graphLocale, "exploreTab")}>
      {mode === "explore" ? (
        <span className={activeClassName} aria-current="page">
          {graphMessage(graphLocale, "exploreTab")}
        </span>
      ) : (
        <Link to="/graph" className={inactiveClassName}>
          {graphMessage(graphLocale, "exploreTab")}
        </Link>
      )}
      {separator}
      {mode === "feed" ? (
        <span className={activeClassName} aria-current="page">
          {graphMessage(graphLocale, "feedTab")}
        </span>
      ) : (
        <Link to="/graph?mode=feed" className={inactiveClassName}>
          {graphMessage(graphLocale, "feedTab")}
        </Link>
      )}
      {separator}
      <Link to="/graph?mode=draft" className={accentClassName}>
        {graphMessage(graphLocale, "createTitle")}
      </Link>
      {separator}
      {mode === "drafts" ? (
        <span className={activeClassName} aria-current="page">
          {draftsLabel}
        </span>
      ) : (
        <Link to="/graph?mode=drafts" className={inactiveClassName}>
          {draftsLabel}
        </Link>
      )}
    </nav>
  );
};

const graphIssueToEntity = (issue: GraphIssue): Issue =>
  ({
    id: issue.id,
    name: issue.name,
    cname: issue.cname,
    properties: issue.properties ?? { subject_properties: {} },
  }) as Issue;

const runBatched = async <TItem, TResult>(
  items: TItem[],
  batchSize: number,
  handler: (item: TItem) => Promise<TResult>
): Promise<TResult[]> => {
  const results: TResult[] = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    results.push(...(await Promise.all(batch.map(handler))));
  }
  return results;
};

/**
 * Loads direct influencers + influenced of a single issue.
 */
const loadDirectInfluences = async (
  issueId: number,
  addData: (issues: GraphIssue[], edges: GraphEdgeSeed[], parentId?: number, parentDepth?: number) => void,
  ensureTotals: (ids: number[]) => Promise<void>
): Promise<void> => {
  const [influencers, influenced] = await Promise.all([
    fetchIssueInfluences(issueId, "influencer", 100),
    fetchIssueInfluences(issueId, "influenced", 100),
  ]);
  const newEdges: GraphEdgeSeed[] = [
    ...influencers.issues.map((n) => ({ sourceId: n.id, targetId: issueId })),
    ...influenced.issues.map((n) => ({ sourceId: issueId, targetId: n.id })),
  ];
  const allIssues = [...influencers.issues, ...influenced.issues];
  addData(allIssues, newEdges, issueId, 0);
  await ensureTotals(allIssues.map((i) => i.id));
};

/* ───────── Component ───────── */

const GraphExplorerInner: React.FC = () => {
  const { cname } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get("mode") ?? "explore";
  const graphLocale = useMemo(
    () => resolveGraphLocale(typeof navigator !== "undefined" ? navigator.language : undefined),
    []
  );
  const draftUi = getDraftUiStrings(resolveDraftLocale());
  const isIssueSpecific = Boolean(cname);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [depth, setDepth] = useState(isIssueSpecific ? 1 : 1);
  /**
   * Viewport-based mobile flag driving graph layout (node handle placement,
   * etc.). Intentionally viewport-based, not `userIsMobile` from UserContext,
   * so desktop window resizes and dev-tools emulation both flip the layout.
   */
  const isMobile = useMobileViewport();
  useHideAppBarOnMobile();
  const feedRootHeightClass = isMobile ? "min-h-screen" : "min-h-[calc(100vh-56px)]";
  const canvasRootHeightClass = isMobile ? "h-screen" : "h-[calc(100vh-56px)]";
  const [addInfluenceOpen, setAddInfluenceOpen] = useState(false);
  const [addInfluenceDirection, setAddInfluenceDirection] = useState<"influencer" | "influenced">("influencer");
  const [addInfluenceCenterIssue, setAddInfluenceCenterIssue] = useState<Issue | null>(null);

  const [nodes, setNodes] = useState<Array<Node<GraphIssueNodeData>>>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [issueTotals, setIssueTotals] = useState<Record<number, InfluenceTotals>>({});
  /**
   * Root/seed node id used by recenter. Kept in state (not just in the ref
   * below) so the GraphCanvas rerenders once the initial load resolves the
   * center issue — otherwise the first recenter tap would fall back to
   * fitView instead of centering on the starting node.
   */
  const [rootNodeId, setRootNodeId] = useState<string | null>(null);

  const nodesByIdRef = useRef<Map<number, GraphNodeEntry>>(new Map());
  const edgesByIdRef = useRef<Map<string, Edge>>(new Map());
  const seedOrderRef = useRef<number[]>([]);
  const issueTotalsRef = useRef<Record<number, InfluenceTotals>>({});

  useEffect(() => {
    setDocumentTitle("Societea - Graph");
    setDefaultSiteOg({ title: "Societea - Graph", url: window.location.href });
  }, []);

  useEffect(() => {
    setDepth(isIssueSpecific ? 1 : 1);
  }, [isIssueSpecific]);

  const resetGraphState = useCallback(() => {
    nodesByIdRef.current = new Map();
    edgesByIdRef.current = new Map();
    seedOrderRef.current = [];
    issueTotalsRef.current = {};
    setNodes([]);
    setEdges([]);
    setIssueTotals({});
    setRootNodeId(null);
  }, []);

  /* ─── Layout engine ─── */

  /**
   * For issue-specific: directed layout with center at (0,0), influencers left, influenced right.
   * For global: lay out each connected component horizontally, stacking components vertically.
   */
  const applyDepthLayout = useCallback(() => {
    const entries = Array.from(nodesByIdRef.current.values());
    if (entries.length === 0) return;

    const edgeList = Array.from(edgesByIdRef.current.values());

    if (isIssueSpecific) {
      const centerId = seedOrderRef.current[0]?.toString();
      if (!centerId) return;

      const dirAdj = new Map<string, Set<string>>();
      const dirRev = new Map<string, Set<string>>();
      edgeList.forEach((e) => {
        if (!dirAdj.has(e.source)) dirAdj.set(e.source, new Set());
        dirAdj.get(e.source)!.add(e.target);
        if (!dirRev.has(e.target)) dirRev.set(e.target, new Set());
        dirRev.get(e.target)!.add(e.source);
      });

      /** BFS upstream from center along reverse edges. */
      const leftDepth = new Map<string, number>();
      leftDepth.set(centerId, 0);
      const lq = [centerId];
      while (lq.length > 0) {
        const id = lq.shift()!;
        const d = leftDepth.get(id) ?? 0;
        (dirRev.get(id) || new Set()).forEach((nid) => {
          if (!leftDepth.has(nid)) {
            leftDepth.set(nid, d + 1);
            lq.push(nid);
          }
        });
      }

      /** BFS downstream from center along forward edges. */
      const rightDepth = new Map<string, number>();
      rightDepth.set(centerId, 0);
      const rq = [centerId];
      while (rq.length > 0) {
        const id = rq.shift()!;
        const d = rightDepth.get(id) ?? 0;
        (dirAdj.get(id) || new Set()).forEach((nid) => {
          if (!rightDepth.has(nid)) {
            rightDepth.set(nid, d + 1);
            rq.push(nid);
          }
        });
      }

      const leftByDepth = new Map<number, string[]>();
      const rightByDepth = new Map<number, string[]>();

      entries.forEach((e) => {
        const id = e.issue.id.toString();
        if (id === centerId) {
          e.depth = 0;
          return;
        }
        const ld = leftDepth.get(id);
        const rd = rightDepth.get(id);
        if (ld !== undefined && ld > 0) {
          e.depth = ld;
          const arr = leftByDepth.get(ld) ?? [];
          arr.push(id);
          leftByDepth.set(ld, arr);
        } else if (rd !== undefined && rd > 0) {
          e.depth = rd;
          const arr = rightByDepth.get(rd) ?? [];
          arr.push(id);
          rightByDepth.set(rd, arr);
        } else {
          e.depth = 1;
        }
      });

      entries.forEach((e) => {
        const id = e.issue.id.toString();
        if (id === centerId) {
          e.position = { x: 0, y: 0 };
          return;
        }
        const ld = leftDepth.get(id);
        if (ld !== undefined && ld > 0) {
          const arr = leftByDepth.get(ld) ?? [id];
          const idx = arr.indexOf(id);
          const total = arr.length;
          const x = -(ld * COL_STEP);
          const y = total <= 1 ? 0 : (idx - (total - 1) / 2) * ROW_STEP;
          e.position = { x, y };
          return;
        }
        const rd = rightDepth.get(id);
        if (rd !== undefined && rd > 0) {
          const arr = rightByDepth.get(rd) ?? [id];
          const idx = arr.indexOf(id);
          const total = arr.length;
          const x = rd * COL_STEP;
          const y = total <= 1 ? 0 : (idx - (total - 1) / 2) * ROW_STEP;
          e.position = { x, y };
        }
      });
    } else {
      /** Global: lay out each connected component, stacked vertically. */
      const adj = new Map<string, Set<string>>();
      const outDeg = new Map<string, number>();
      edgeList.forEach((e) => {
        if (!adj.has(e.source)) adj.set(e.source, new Set());
        adj.get(e.source)!.add(e.target);
        if (!adj.has(e.target)) adj.set(e.target, new Set());
        adj.get(e.target)!.add(e.source);
        outDeg.set(e.source, (outDeg.get(e.source) ?? 0) + 1);
      });

      /** Find connected components. */
      const visited = new Set<string>();
      const components: string[][] = [];
      entries.forEach((e) => {
        const startId = e.issue.id.toString();
        if (visited.has(startId)) return;
        const comp: string[] = [];
        const queue = [startId];
        visited.add(startId);
        while (queue.length > 0) {
          const id = queue.shift()!;
          comp.push(id);
          (adj.get(id) || new Set()).forEach((nid) => {
            if (!visited.has(nid)) {
              visited.add(nid);
              queue.push(nid);
            }
          });
        }
        components.push(comp);
      });

      /** Sort components: largest (most connected) first. */
      components.sort((a, b) => b.length - a.length);

      const nodeMap = new Map(entries.map((e) => [e.issue.id.toString(), e]));
      let globalOffsetY = 0;

      components.forEach((comp) => {
        /** Pick hub: highest out-degree in this component. */
        let hubId = comp[0];
        let maxOut = 0;
        comp.forEach((id) => {
          const out = outDeg.get(id) ?? 0;
          if (out > maxOut) {
            maxOut = out;
            hubId = id;
          }
        });

        /** BFS from hub for depth. */
        const depthMap = new Map<string, number>();
        depthMap.set(hubId, 0);
        const queue = [hubId];
        while (queue.length > 0) {
          const id = queue.shift()!;
          const d = depthMap.get(id) ?? 0;
          (adj.get(id) || new Set()).forEach((nid) => {
            if (!depthMap.has(nid)) {
              depthMap.set(nid, d + 1);
              queue.push(nid);
            }
          });
        }

        /** Group by depth. */
        const byDepth = new Map<number, string[]>();
        comp.forEach((id) => {
          const d = depthMap.get(id) ?? 0;
          const entry = nodeMap.get(id);
          if (entry) entry.depth = d;
          const arr = byDepth.get(d) ?? [];
          arr.push(id);
          byDepth.set(d, arr);
        });

        /** Lay out: x by depth column, y evenly spaced, offset by globalOffsetY. */
        let compMaxY = 0;
        byDepth.forEach((ids, d) => {
          ids.forEach((id, idx) => {
            const entry = nodeMap.get(id);
            if (!entry) return;
            const x = d * COL_STEP;
            const y = globalOffsetY + idx * ROW_STEP;
            entry.position = { x, y };
            compMaxY = Math.max(compMaxY, y + NODE_H);
          });
        });

        globalOffsetY = compMaxY + GROUP_GAP;
      });

      /** Handle orphan nodes (no edges). */
      entries.forEach((e) => {
        const id = e.issue.id.toString();
        if (!adj.has(id) && e.position.x === 0 && e.position.y === 0) {
          e.position = { x: 0, y: globalOffsetY };
          e.depth = 0;
          globalOffsetY += ROW_STEP;
        }
      });
    }
  }, [isIssueSpecific]);

  const refreshNodes = useCallback(() => {
    setNodes(
      Array.from(nodesByIdRef.current.values()).map((entry) => ({
        id: entry.issue.id.toString(),
        type: "issueNode",
        position: entry.position,
        data: {
          entity: graphIssueToEntity(entry.issue),
          additionalInfluencersCount: 0,
          additionalInfluencedCount: 0,
          totalInfluencers: 0,
          totalInfluenced: 0,
          onExpandInfluencers: () => undefined,
          onExpandInfluenced: () => undefined,
          locale: graphLocale,
        },
      }))
    );
  }, [graphLocale]);

  const addGraphData = useCallback(
    (
      issues: GraphIssue[],
      newEdges: GraphEdgeSeed[],
      parentId?: number,
      parentDepth?: number
    ) => {
      let didAddNode = false;
      let didAddEdge = false;
      const assignDepth = parentId === undefined ? 0 : (parentDepth ?? 0) + 1;
      let indexAtDepth = 0;

      issues.forEach((issue) => {
        if (!nodesByIdRef.current.has(issue.id)) {
          nodesByIdRef.current.set(issue.id, {
            issue,
            position: { x: indexAtDepth * COL_STEP, y: 0 },
            depth: assignDepth,
          });
          indexAtDepth += 1;
          didAddNode = true;
        }
      });

      const unique = dedupeEdges(newEdges);
      unique.forEach((edge) => {
        const edgeId = buildEdgeId(edge.sourceId, edge.targetId);
        if (edgesByIdRef.current.has(edgeId)) return;
        edgesByIdRef.current.set(edgeId, {
          id: edgeId,
          source: edge.sourceId.toString(),
          target: edge.targetId.toString(),
          markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
        });
        didAddEdge = true;
      });

      if (didAddNode || didAddEdge) {
        if (didAddEdge) setEdges(Array.from(edgesByIdRef.current.values()));
        if (didAddNode) {
          applyDepthLayout();
        }
        refreshNodes();
      }
    },
    [refreshNodes, applyDepthLayout]
  );

  const commitIssueTotals = useCallback(
    (updates: Record<number, InfluenceTotals>) => {
      setIssueTotals((prev) => {
        const next = { ...prev, ...updates };
        issueTotalsRef.current = next;
        return next;
      });
    },
    []
  );

  const ensureIssueTotals = useCallback(
    async (issueIds: number[]) => {
      const missing = issueIds.filter((id) => !issueTotalsRef.current[id]);
      if (missing.length === 0) return;
      const results = await runBatched(
        missing,
        DEFAULT_BATCH_SIZE,
        async (issueId) => ({
          issueId,
          totals: await fetchIssueInfluenceTotals(issueId),
        })
      );
      const updates: Record<number, InfluenceTotals> = {};
      results.forEach((r) => {
        updates[r.issueId] = r.totals;
      });
      commitIssueTotals(updates);
    },
    [commitIssueTotals]
  );

  const expandIssue = useCallback(
    async (issueId: number, direction: InfluenceDirection) => {
      try {
        setErrorMessage(null);
        const parentEntry = nodesByIdRef.current.get(issueId);
        const parentDepth = parentEntry?.depth ?? 0;
        const result = await fetchIssueInfluences(issueId, direction, 100);
        const newEdges: GraphEdgeSeed[] = result.issues.map((issue) => ({
          sourceId: direction === "influencer" ? issue.id : issueId,
          targetId: direction === "influencer" ? issueId : issue.id,
        }));
        addGraphData(
          [parentEntry?.issue, ...result.issues].filter(
            (issue): issue is GraphIssue => Boolean(issue)
          ),
          newEdges,
          issueId,
          parentDepth
        );
        await ensureIssueTotals(result.issues.map((i) => i.id));

        /** Auto-increase depth so newly expanded nodes are visible. */
        const requiredDepth = parentDepth + 1;
        setDepth((d) => Math.max(d, requiredDepth));
      } catch (error) {
        console.error("Failed to expand issue graph:", error);
        setErrorMessage("Failed to expand issue connections.");
      }
    },
    [addGraphData, ensureIssueTotals]
  );

  const openAddInfluence = useCallback(
    (issue: Issue, direction: "influencer" | "influenced") => {
      setAddInfluenceCenterIssue(issue);
      setAddInfluenceDirection(direction);
      setAddInfluenceOpen(true);
    },
    []
  );

  /**
   * When the InfluencesModal closes, incrementally load any new influences
   * for the center issue so the graph updates without a full reload.
   * Uses the center issue's actual graph depth for correct positioning.
   */
  const handleInfluencesModalClose = useCallback(async () => {
    const centerIssue = addInfluenceCenterIssue;
    setAddInfluenceOpen(false);
    setAddInfluenceCenterIssue(null);

    if (!centerIssue) return;

    try {
      const parentEntry = nodesByIdRef.current.get(centerIssue.id);
      const parentDepth = parentEntry?.depth ?? 0;

      const [influencers, influenced] = await Promise.all([
        fetchIssueInfluences(centerIssue.id, "influencer", 100),
        fetchIssueInfluences(centerIssue.id, "influenced", 100),
      ]);

      const newEdges: GraphEdgeSeed[] = [
        ...influencers.issues.map((n) => ({ sourceId: n.id, targetId: centerIssue.id })),
        ...influenced.issues.map((n) => ({ sourceId: centerIssue.id, targetId: n.id })),
      ];
      const allNewIssues = [...influencers.issues, ...influenced.issues];

      addGraphData(allNewIssues, newEdges, centerIssue.id, parentDepth);

      /** Force-refresh totals for the center issue (counts changed) and any new nodes. */
      const idsToRefresh = [centerIssue.id, ...allNewIssues.map((i) => i.id)];
      idsToRefresh.forEach((id) => { delete issueTotalsRef.current[id]; });
      await ensureIssueTotals(idsToRefresh);

      /** Auto-increase depth so newly-added nodes are visible. */
      setDepth((d) => Math.max(d, parentDepth + 1));
    } catch (err) {
      console.error("Failed to refresh influences after modal close:", err);
    }
  }, [addInfluenceCenterIssue, addGraphData, ensureIssueTotals]);

  /* ─── Initial load ─── */

  const loadInitialGraph = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    resetGraphState();

    try {
      if (cname) {
        /** Issue-specific: load center issue + direct influences. */
        const issue = await fetchIssueByCname(cname);
        seedOrderRef.current = [issue.id];
        setRootNodeId(String(issue.id));
        addGraphData([issue], [], undefined, 0);
        await ensureIssueTotals([issue.id]);
        await loadDirectInfluences(issue.id, addGraphData, ensureIssueTotals);
      } else {
        /** Global: load ALL connected components from backend. */
        const groups = await fetchGraphRoots();
        if (groups.length > 0) {
          const allIssues: GraphIssue[] = [];
          const allEdges: GraphEdgeSeed[] = [];
          const rootIds: number[] = [];

          groups.forEach((group: GraphRootsGroup) => {
            (group.issues ?? []).forEach((i) => {
              if (!allIssues.some((existing) => existing.id === i.id)) {
                allIssues.push({
                  id: i.id,
                  name: i.name,
                  cname: i.cname,
                  properties: i.properties,
                });
              }
            });
            (group.edges ?? []).forEach((e) => allEdges.push(e));
            (group.roots ?? []).forEach((r) => {
              if (!rootIds.includes(r)) rootIds.push(r);
            });
          });

          seedOrderRef.current = rootIds;
          /**
           * Global graphs do not have a single canonical root; the recenter
           * button falls back to fitView when rootNodeId is null, so we
           * intentionally leave it unset here instead of picking an arbitrary
           * component hub which would bias the user's view.
           */
          setRootNodeId(null);
          addGraphData(allIssues, allEdges, undefined, 0);
          await ensureIssueTotals(allIssues.map((i) => i.id));
        } else {
          /** Fallback: issue rankings. */
          const { data } = await getIssueRankings();
          const rankings = Array.isArray(data) ? data : [];
          const seedIssues: GraphIssue[] = rankings
            .filter((r): r is IssueRanking => Boolean(r))
            .map((r) => ({
              id: r.issueId,
              name: r.issue?.name || `Issue ${r.issueId}`,
              cname: r.issue?.cname || `${r.issueId}`,
              properties: r.issue?.properties,
            }));
          seedOrderRef.current = seedIssues.map((i) => i.id);
          setRootNodeId(null);
          addGraphData(seedIssues, [], undefined, 0);
          await ensureIssueTotals(seedIssues.map((i) => i.id));
          await runBatched(seedIssues, DEFAULT_BATCH_SIZE, async (issue) => {
            await loadDirectInfluences(issue.id, addGraphData, ensureIssueTotals);
          });
        }
      }
    } catch (error) {
      console.error("Failed to load graph:", error);
      setErrorMessage("Failed to load issue graph.");
    } finally {
      setIsLoading(false);
    }
  }, [addGraphData, cname, ensureIssueTotals, resetGraphState]);

  useEffect(() => {
    loadInitialGraph();
  }, [loadInitialGraph]);

  /* ─── Connected component for group drag ─── */

  const getConnectedComponentIds = useCallback(
    (nodeId: string, maxDepth: number): Set<string> => {
      const component = new Set<string>();
      const queue = [nodeId];
      component.add(nodeId);
      const edgeList = Array.from(edgesByIdRef.current.values());
      const depthOk = (id: string) => {
        const entry = nodesByIdRef.current.get(Number(id));
        return entry && entry.depth <= maxDepth;
      };
      const adj = new Map<string, Set<string>>();
      edgeList.forEach((e) => {
        if (!depthOk(e.source) || !depthOk(e.target)) return;
        if (!adj.has(e.source)) adj.set(e.source, new Set());
        adj.get(e.source)!.add(e.target);
        if (!adj.has(e.target)) adj.set(e.target, new Set());
        adj.get(e.target)!.add(e.source);
      });
      while (queue.length > 0) {
        const id = queue.shift()!;
        (adj.get(id) || new Set()).forEach((nid) => {
          if (!component.has(nid)) {
            component.add(nid);
            queue.push(nid);
          }
        });
      }
      return component;
    },
    []
  );

  const moveConnectedGroup = useCallback(
    (nodeId: string, dx: number, dy: number) => {
      setNodes((current) => {
        const componentIds = getConnectedComponentIds(nodeId, depth);
        const nodeMap = new Map(current.map((n) => [n.id, n]));
        componentIds.forEach((id) => {
          const n = nodeMap.get(id);
          if (n?.position) {
            const newPos = { x: n.position.x + dx, y: n.position.y + dy };
            nodeMap.set(id, { ...n, position: newPos });
            const entry = nodesByIdRef.current.get(Number(id));
            if (entry) entry.position = newPos;
          }
        });
        return Array.from(nodeMap.values());
      });
    },
    [getConnectedComponentIds, depth]
  );

  /* ─── Sync node data whenever edges/totals/depth change ─── */

  useEffect(() => {
    /**
     * Compute edge counts using only edges between currently-visible nodes.
     * This ensures the "additional" badge reflects what the user actually sees,
     * so the expand button remains active when nodes exist but are hidden by depth.
     */
    const visibleIds = new Set(
      Array.from(nodesByIdRef.current.entries())
        .filter(([, entry]) => entry.depth <= depth)
        .map(([id]) => id.toString())
    );
    const visibleEdgesForCounts = edges.filter(
      (e) => visibleIds.has(e.source) && visibleIds.has(e.target)
    );
    const edgeCounts = computeEdgeCounts(visibleEdgesForCounts);

    setNodes((current) =>
      current.map((node) => {
        const entry = nodesByIdRef.current.get(Number(node.id));
        if (!entry) return node;
        const counts = edgeCounts[node.id] || { inCount: 0, outCount: 0 };
        const totals = issueTotals[entry.issue.id];
        const totalIn = totals?.influencersTotal ?? 0;
        const totalOut = totals?.influencedTotal ?? 0;
        const additionalInfluencersCount = Math.max(0, totalIn - counts.inCount);
        const additionalInfluencedCount = Math.max(0, totalOut - counts.outCount);
        const entity = graphIssueToEntity(entry.issue);
        return {
          ...node,
          data: {
            entity,
            additionalInfluencersCount,
            additionalInfluencedCount,
            totalInfluencers: totalIn,
            totalInfluenced: totalOut,
            onExpandInfluencers: (id: number) => expandIssue(id, "influencer"),
            onExpandInfluenced: (id: number) => expandIssue(id, "influenced"),
            onAddInfluencer: () => openAddInfluence(entity, "influencer"),
            onAddInfluenced: () => openAddInfluence(entity, "influenced"),
            onGroupDragDelta: (dx: number, dy: number) =>
              moveConnectedGroup(node.id, dx, dy),
            isMobile,
            locale: graphLocale,
          },
        };
      })
    );
  }, [edges, expandIssue, issueTotals, openAddInfluence, moveConnectedGroup, isMobile, depth, graphLocale]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      setNodes((current) => {
        const next = applyNodeChanges(changes, current);
        next.forEach((node) => {
          const entry = nodesByIdRef.current.get(Number(node.id));
          if (entry) entry.position = node.position;
        });
        return next;
      });
    },
    []
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => setEdges((c) => applyEdgeChanges(changes, c)),
    []
  );

  /* ─── Visibility filtering by depth ─── */

  const isEmpty = !isLoading && nodes.length === 0;

  const visibleNodeIds = new Set(
    nodes
      .filter((n) => {
        const entry = nodesByIdRef.current.get(Number(n.id));
        return entry && entry.depth <= depth;
      })
      .map((n) => n.id)
  );
  const visibleNodes = nodes.filter((n) => visibleNodeIds.has(n.id));
  const visibleEdges = edges.filter(
    (e) => visibleNodeIds.has(e.source) && visibleNodeIds.has(e.target)
  );

  const handleOpenGraphDraft = (draft: DbDraft | LocalDraftSummary) => {
    if (draft.source === "db") {
      navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(draft.id))}`);
    }
  };

  if (mode === "draft") {
    return <GraphDraftEditor />;
  }

  if (mode === "drafts") {
    return (
      <div className={`graph-explorer-root relative flex ${feedRootHeightClass} w-full flex-col`}>
        <ExperimentalFeatureBanner featureId="graph-explorer" />
        <GraphNav mode="drafts" graphLocale={graphLocale} draftsLabel={draftUi.draftsTab} />
        <div className="mx-auto w-full max-w-3xl p-4">
          <DraftsList
            heading={draftUi.graphDraftsHeading}
            filters={{ source: "post", kind: "collection", collectionType: "graph" }}
            onOpenDraft={handleOpenGraphDraft}
          />
        </div>
      </div>
    );
  }

  if (mode === "edit") {
    // Legacy: `mode=edit` without a collection id used to open a blank create
    // surface via the ad hoc `?blank=1` hint. The draft flow now owns that
    // surface, so redirect to `mode=draft` to keep bookmarked links working.
    const collectionIdParam = searchParams.get("collectionId");
    if (!collectionIdParam) {
      return <GraphDraftEditor />;
    }
    return <GraphExplorerEdit />;
  }

  if (mode === "feed") {
    return (
      <div className={`graph-explorer-root relative flex ${feedRootHeightClass} w-full flex-col`}>
        <ExperimentalFeatureBanner featureId="graph-explorer" />
        <GraphNav mode="feed" graphLocale={graphLocale} draftsLabel={draftUi.draftsTab} />
        <GraphExplorerFeed />
      </div>
    );
  }

  return (
    <div className={`graph-explorer-root relative ${canvasRootHeightClass} w-full flex flex-col`}>
      <ExperimentalFeatureBanner featureId="graph-explorer" />
      <GraphNav mode="explore" graphLocale={graphLocale} draftsLabel={draftUi.draftsTab} />
      <div className="graph-explorer-content flex-1 min-h-0">
        {isLoading && (
          <LoadingSkeleton
            variant="graph"
            ariaLabel={graphMessage(graphLocale, "loadingGraph")}
            containerClassName="graph-explorer-loading"
          />
        )}
        {!isLoading && (
          <div className="graph-explorer-canvas relative h-full w-full">
            {errorMessage && (
              <div className="graph-explorer-error px-4 py-2 text-sm text-warning-red dark:text-warning-red-dark">
                {errorMessage}
              </div>
            )}
            {isEmpty ? (
              <div className="graph-explorer-empty flex items-center justify-center h-full text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {graphMessage(graphLocale, "feedEmptyHint")}
              </div>
            ) : (
              <GraphCanvas
                nodes={visibleNodes}
                edges={visibleEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                depth={depth}
                onDepthUp={() => setDepth((d) => Math.min(5, d + 1))}
                onDepthDown={() => setDepth((d) => Math.max(0, d - 1))}
                locale={graphLocale}
                rootNodeId={rootNodeId}
              />
            )}
          </div>
        )}
      </div>
      {addInfluenceOpen && addInfluenceCenterIssue && (
        <InfluencesModal
          issueId={addInfluenceCenterIssue.id}
          onClose={handleInfluencesModalClose}
          initialTab={addInfluenceDirection}
        />
      )}
    </div>
  );
};

export const GraphExplorer: React.FC = () => (
  <ReactFlowProvider>
    <GraphExplorerInner />
  </ReactFlowProvider>
);

export default GraphExplorer;
